/**
 * Geographic calculation utilities.
 * Provides shared functions for distance calculations, coordinate transformations,
 * and other geographic computations.
 */

import type { GeoLocation, LatLngLiteral } from '../types'

/**
 * Convert degrees to radians.
 *
 * @param degrees - Angle in degrees
 * @returns Angle in radians
 */
export function toRadians(degrees: number): number {
  return degrees * (Math.PI / 180)
}

/**
 * Convert radians to degrees.
 *
 * @param radians - Angle in radians
 * @returns Angle in degrees
 */
export function toDegrees(radians: number): number {
  return radians * (180 / Math.PI)
}

/**
 * Linear interpolation between two values.
 *
 * @param start - Starting value
 * @param end - Ending value
 * @param t - Interpolation factor (0 = start, 1 = end)
 * @returns Interpolated value
 */
export function lerp(start: number, end: number, t: number): number {
  return start + (end - start) * t
}

/**
 * Easing function type for animations.
 */
export type EasingFunction = (t: number) => number

/**
 * Standard easing functions for animations.
 *
 * @example
 * ```typescript
 * const t = elapsed / duration
 * const easedT = easingFunctions['ease-out'](t)
 * const value = lerp(start, end, easedT)
 * ```
 */
export const easingFunctions: Record<string, EasingFunction> = {
  linear: (t: number) => t,
  'ease-in': (t: number) => t * t,
  'ease-out': (t: number) => t * (2 - t),
  'ease-in-out': (t: number) => (t < 0.5 ? 2 * t * t : -1 + (4 - 2 * t) * t),
}

/**
 * Linear interpolation between two angles, taking the shortest path.
 *
 * Handles wrap-around correctly: lerping from 350° to 10° goes through 0°
 * (20° total) rather than the long way (340° total).
 *
 * @param start - Starting angle in degrees
 * @param end - Ending angle in degrees
 * @param t - Interpolation factor (0 = start, 1 = end)
 * @returns Interpolated angle in degrees (normalized to 0-360)
 */
export function lerpAngle(start: number, end: number, t: number): number {
  // Normalize both angles to 0-360
  const normalizedStart = ((start % 360) + 360) % 360
  const normalizedEnd = ((end % 360) + 360) % 360

  // Find the shortest path
  let delta = normalizedEnd - normalizedStart

  // If delta > 180, go the other way
  if (delta > 180) {
    delta -= 360
  } else if (delta < -180) {
    delta += 360
  }

  // Interpolate and normalize result
  const result = normalizedStart + delta * t
  return ((result % 360) + 360) % 360
}

/**
 * Calculate distance between two coordinates using the Haversine formula.
 * Returns distance in meters.
 *
 * Supports both GeoLocation ({latitude, longitude}) and LatLngLiteral ({lat, lng}) formats.
 *
 * @param coord1 - First coordinate
 * @param coord2 - Second coordinate
 * @returns Distance in meters
 */
export function calculateDistance(
  coord1: GeoLocation | LatLngLiteral,
  coord2: GeoLocation | LatLngLiteral
): number {
  // Normalize to lat/lng values
  const lat1 = 'lat' in coord1 ? coord1.lat : coord1.latitude
  const lng1 = 'lng' in coord1 ? coord1.lng : coord1.longitude
  const lat2 = 'lat' in coord2 ? coord2.lat : coord2.latitude
  const lng2 = 'lng' in coord2 ? coord2.lng : coord2.longitude

  const R = 6371000 // Earth's radius in meters
  const dLat = toRadians(lat2 - lat1)
  const dLng = toRadians(lng2 - lng1)

  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(toRadians(lat1)) *
      Math.cos(toRadians(lat2)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2)

  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

/**
 * Check if two coordinates are approximately equal within a tolerance.
 *
 * Supports both GeoLocation ({latitude, longitude}) and LatLngLiteral ({lat, lng}) formats.
 *
 * @param coord1 - First coordinate
 * @param coord2 - Second coordinate
 * @param toleranceMeters - Tolerance in meters (default: 10m)
 * @returns True if coordinates are within tolerance
 */
export function areCoordinatesEqual(
  coord1: GeoLocation | LatLngLiteral,
  coord2: GeoLocation | LatLngLiteral,
  toleranceMeters: number = 10
): boolean {
  return calculateDistance(coord1, coord2) <= toleranceMeters
}

/**
 * Calculate the center point between multiple coordinates.
 *
 * Supports both GeoLocation ({latitude, longitude}) and LatLngLiteral ({lat, lng}) formats.
 * Returns the same format as the input.
 *
 * @param coordinates - Array of coordinates
 * @returns Center coordinate in the same format as input, or null if array is empty
 */
export function calculateCenterPoint(
  coords: GeoLocation[]
): GeoLocation | null
export function calculateCenterPoint(
  coords: LatLngLiteral[]
): LatLngLiteral | null
export function calculateCenterPoint(
  coords: (GeoLocation | LatLngLiteral)[]
): GeoLocation | LatLngLiteral | null {
  if (coords.length === 0) return null

  // Detect format from first element
  const isLatLng = 'lat' in coords[0]

  if (isLatLng) {
    const latLngCoords = coords as LatLngLiteral[]
    const sum = latLngCoords.reduce(
      (acc, coord) => ({
        lat: acc.lat + coord.lat,
        lng: acc.lng + coord.lng,
      }),
      { lat: 0, lng: 0 }
    )
    return {
      lat: sum.lat / latLngCoords.length,
      lng: sum.lng / latLngCoords.length,
    }
  } else {
    const geoLocations = coords as GeoLocation[]
    const sum = geoLocations.reduce(
      (acc, loc) => ({
        latitude: acc.latitude + loc.latitude,
        longitude: acc.longitude + loc.longitude,
      }),
      { latitude: 0, longitude: 0 }
    )
    return {
      latitude: sum.latitude / geoLocations.length,
      longitude: sum.longitude / geoLocations.length,
    }
  }
}
